/**
 * Environment State — Types & Initial Values
 *
 * Defines the shape of the env slice and its starting state.
 * The env slice holds environment configuration that affects
 * how the app connects to APIs and renders (region, stage, theme).
 */
import {
  getRegionFromUrl, getStageFromUrl, getApiBase, isGlobalRegion,
} from '../../../constants';

/** Light or dark mode */
export type Theme = 'light' | 'dark';

/**
 * Base URL for a single-region query. Global search has no single host, so it
 * resolves to '' and the fanning-out thunks read getApiBases(stage) instead.
 */
export const resolveApiBase = (stage: string, region: string): string =>
  (isGlobalRegion(region) ? '' : getApiBase(stage, region));

/** Shape of the env slice state */
export interface EnvState {
  /** Selected AWS region (e.g. 'us-east-1'), or GLOBAL_REGION for all regions */
  region: string;
  /** Deployment stage derived from URL (Alpha, Beta, Gamma, Prod) */
  stage: string;
  /** Resolved API base URL for the current region + stage; '' in global search */
  apiBase: string;
  /** True when region is GLOBAL_REGION — queries fan out to every region */
  global: boolean;
  /** UI theme preference */
  theme: Theme;
}

const stage = getStageFromUrl();
const region = getRegionFromUrl();

/** Initial state — region & stage are restored from the URL so they survive a refresh */
export const envInitialState: EnvState = {
  region,
  stage,
  apiBase: resolveApiBase(stage, region),
  global: isGlobalRegion(region),
  theme: 'light',
};
